import { useQuery } from '@tanstack/react-query'

import { cbGetOnrampOptions } from '@src/pages/FiatExchange/api/coinbase.api'
import { DEFAULT_ONRAMP_FIAT_CURRENCY } from '@src/pages/FiatExchange/config'
import { PaymentMethod } from '@src/types/coinbase'
import { logger } from '@src/utils/logger'

/**
 * Hook to fetch available payment options for onramp transactions
 *
 * @param country - ISO country code (e.g., 'US')
 * @param fiatCurrency - Optional fiat currency to filter by (e.g., 'USD')
 * @param enabled - Whether the query should auto-execute
 * @param prefetch - Whether to fetch all payment methods or only for a specific currency
 */
export const useOnrampPaymentOptions = (country: string, fiatCurrency?: string, enabled = true, prefetch = false) => {
    // Use DEFAULT_ONRAMP_FIAT_CURRENCY if fiatCurrency is not provided but prefetch is true
    const effectiveCurrency = prefetch && !fiatCurrency ? DEFAULT_ONRAMP_FIAT_CURRENCY.symbol : fiatCurrency

    return useQuery({
        queryKey: prefetch
            ? ['onramp-payment-methods', 'prefetch', country, effectiveCurrency]
            : ['onramp-payment-methods', effectiveCurrency, country],
        enabled: enabled,
        staleTime: 5 * 60 * 1000, // 5 minutes cache
        queryFn: async () => {
            try {
                logger.info(
                    `Fetching onramp payment options for country: ${country}, currency: ${fiatCurrency || 'any'}`
                )
                const response = await cbGetOnrampOptions(country)
                logger.debug('cbGetOnrampOptions response:', response)

                // Initialize empty payment methods array
                let paymentMethods: PaymentMethod[] = []

                // Handle the API response to extract payment methods
                if (response && response.payment_currencies) {
                    // Use type assertion to safely access the payment currencies data
                    const currencies = response.payment_currencies as Record<string, any>

                    // If we have an effective currency (either provided or default), use it directly
                    if (effectiveCurrency && currencies[effectiveCurrency]) {
                        const currencyData = currencies[effectiveCurrency]
                        if (currencyData?.limits) {
                            const limits = currencyData.limits as Array<{ id: string; min: string; max: string }>

                            // Create payment method objects from the limits
                            paymentMethods = limits.map(limit => ({
                                id: limit.id,
                                description: `Buy using ${limit.id.split('_').join(' ').toLowerCase()}`,
                                limits: [
                                    {
                                        id: limit.id,
                                        min: limit.min,
                                        max: limit.max,
                                        currency: effectiveCurrency,
                                    },
                                ],
                            }))
                        }
                    }
                    // If no effective currency or it's not found, fall back to collecting all methods
                    else if (prefetch) {
                        // Collect unique payment methods across all currencies
                        const uniqueMethods = new Map<string, PaymentMethod>()

                        Object.entries(currencies).forEach(([currencyCode, currencyData]: [string, any]) => {
                            if (currencyData?.limits) {
                                const limits = currencyData.limits as Array<{ id: string; min: string; max: string }>

                                limits.forEach(limit => {
                                    // Only add if not already added (prioritize first occurrence)
                                    if (!uniqueMethods.has(limit.id)) {
                                        uniqueMethods.set(limit.id, {
                                            id: limit.id,
                                            description: `Buy using ${limit.id.split('_').join(' ').toLowerCase()}`,
                                            limits: [
                                                {
                                                    id: limit.id,
                                                    min: limit.min,
                                                    max: limit.max,
                                                    currency: currencyCode,
                                                },
                                            ],
                                        })
                                    }
                                })
                            }
                        })

                        paymentMethods = Array.from(uniqueMethods.values())
                    }
                    // Currency-specific mode for when no default was provided
                    else if (!effectiveCurrency) {
                        // Find the first available currency if none was specified
                        const currencyKey = Object.keys(currencies)[0]

                        // If we have limits for this currency, extract the payment methods
                        if (currencyKey && currencies[currencyKey]?.limits) {
                            const currencyData = currencies[currencyKey]
                            const limits = currencyData.limits as Array<{ id: string; min: string; max: string }>

                            // Create payment method objects from the limits
                            paymentMethods = limits.map(limit => ({
                                id: limit.id,
                                description: `Buy using ${limit.id.split('_').join(' ').toLowerCase()}`,
                                limits: [
                                    {
                                        id: limit.id,
                                        min: limit.min,
                                        max: limit.max,
                                        currency: currencyKey,
                                    },
                                ],
                            }))
                        }
                    }
                }

                logger.debug('Onramp payment methods:', paymentMethods)
                return paymentMethods
            } catch (error) {
                logger.error('Error fetching onramp payment options:', error)
                return []
            }
        },
    })
}
